import SeoMeta from 'components/Basic/Head/SeoMeta';
import CommonLayout from 'components/Layout/CommonLayout';
import { Webline } from 'components/Layout/Webline/Webline';
import B2BLogin from 'components/Pages/B2BLogin/B2BLogin';
import checkIsLoggedOnSSR from 'helpers/checkIsLoggedOnSSR';
import { deleteReturnUrlCookie, getReturnUrlCookie } from 'helpers/cookies/returnUrlCookieHelper';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { GetServerSidePropsResult } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { NextPageExtended } from 'types/global';

type B2BLoginPageProps = {
    referringUrl: string | null;
};

const B2BLoginPage: NextPageExtended<B2BLoginPageProps & ServerSidePropsType> = ({ referringUrl }) => {
    const { t } = useTranslation();

    return (
        <CommonLayout withoutMoveUpActionButton title={t('Login')}>
            <Webline className="mb-20">
                <SeoMeta title={t('Login')} />
                <B2BLogin referringUrl={referringUrl} />
            </Webline>
        </CommonLayout>
    );
};

B2BLoginPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const returnUrl = getReturnUrlCookie(context);

            const client = createClient({
                ssrExchange,
                redisClient,
                context,
                t,
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
            });

            const serverSideProps = await initServerSideProps({ context, domainConfig, ssrExchange, client });

            const isLogged = checkIsLoggedOnSSR(client);

            if (isLogged) {
                const redirectUrl = returnUrl ?? '/';
                deleteReturnUrlCookie(context);

                return {
                    redirect: {
                        statusCode: 302,
                        destination: redirectUrl,
                    },
                };
            }

            return {
                ...serverSideProps,
                props: { ...('props' in serverSideProps ? serverSideProps.props : {}), referringUrl: returnUrl },
            } as GetServerSidePropsResult<B2BLoginPageProps>;
        },
);

export default B2BLoginPage;
